import { analysis_reference_area_enum, emission_factor_source_enum } from '@predium/enums';
import { assign, enqueueActions, sendTo, setup } from 'xstate';
import { localStorageSyncActorPlaceholder } from '@/components/organisms/AnalysisSettings/machines/localStorageSyncActor';
import { urlSyncActorPlaceholder } from '@/components/organisms/AnalysisSettings/machines/urlSyncActor';

export type DisplayBase = 'per_square_meter' | 'absolute';

export const DEFAULT_DISPLAY_BASE: DisplayBase = 'per_square_meter';
export const DEFAULT_REFERENCE_AREA: analysis_reference_area_enum = analysis_reference_area_enum.EBF;
export const DEFAULT_REFERENCE_AREA_CONSUMPTION: analysis_reference_area_enum = analysis_reference_area_enum.BGF;

export const getDefaultReferenceArea = (isConsumptionAnalysis: boolean): analysis_reference_area_enum =>
  isConsumptionAnalysis ? DEFAULT_REFERENCE_AREA_CONSUMPTION : DEFAULT_REFERENCE_AREA;
export const DEFAULT_EMISSION_FACTOR_SOURCE: emission_factor_source_enum =
  emission_factor_source_enum.NATIONAL_ENERGY_LAW;

type AnalysisSettingsContext = {
  scope: 'statusQuo' | 'actionPlanning';
  tracksIncludeNewConstruction: boolean;
  appliedDisplayBase: DisplayBase;
  pendingDisplayBase: DisplayBase;
  appliedReferenceArea: analysis_reference_area_enum;
  pendingReferenceArea: analysis_reference_area_enum;
  defaultReferenceArea: analysis_reference_area_enum;
  appliedEmissionFactorSource: emission_factor_source_enum;
  pendingEmissionFactorSource: emission_factor_source_enum;
  appliedIncludeNewConstruction: boolean;
  pendingIncludeNewConstruction: boolean;
  defaultIncludeNewConstruction: boolean;
};

type AnalysisSettingsEvent =
  | {
      type: 'LOCALSTORAGE_INITIALIZED';
      displayBase: DisplayBase;
      referenceArea: analysis_reference_area_enum;
      emissionFactorSource: emission_factor_source_enum;
      includeNewConstruction: boolean;
    }
  | { type: 'USER_CHANGE_PENDING_DISPLAY_BASE'; displayBase: DisplayBase }
  | { type: 'USER_CHANGE_PENDING_REFERENCE_AREA'; referenceArea: analysis_reference_area_enum }
  | { type: 'USER_CHANGE_PENDING_EMISSION_FACTOR_SOURCE'; emissionFactorSource: emission_factor_source_enum }
  | { type: 'USER_CHANGE_PENDING_INCLUDE_NEW_CONSTRUCTION'; includeNewConstruction: boolean }
  | { type: 'ORG_DEFAULT_INCLUDE_PLANNED_UPDATED'; defaultIncludeNewConstruction: boolean }
  | { type: 'USER_APPLY'; close: VoidFunction }
  | { type: 'USER_RESET' }
  | { type: 'USER_CLOSE'; close: VoidFunction };

export function isModifiedContext(context: AnalysisSettingsContext): boolean {
  return (
    context.pendingDisplayBase !== context.appliedDisplayBase ||
    context.pendingReferenceArea !== context.appliedReferenceArea ||
    context.pendingEmissionFactorSource !== context.appliedEmissionFactorSource ||
    (context.tracksIncludeNewConstruction &&
      context.pendingIncludeNewConstruction !== context.appliedIncludeNewConstruction)
  );
}

function shouldWriteUrlOnInitPredicate(context: AnalysisSettingsContext, event: AnalysisSettingsEvent): boolean {
  if (event.type !== 'LOCALSTORAGE_INITIALIZED') return false;
  return (
    context.scope === 'statusQuo' ||
    event.displayBase !== context.appliedDisplayBase ||
    event.referenceArea !== context.appliedReferenceArea ||
    event.emissionFactorSource !== context.appliedEmissionFactorSource ||
    (context.tracksIncludeNewConstruction && event.includeNewConstruction !== context.appliedIncludeNewConstruction)
  );
}

export type AnalysisSettingsMachineInput = {
  scope: 'statusQuo' | 'actionPlanning';
  initialDisplayBase: DisplayBase;
  initialReferenceArea: analysis_reference_area_enum;
  defaultReferenceArea: analysis_reference_area_enum;
  initialEmissionFactorSource: emission_factor_source_enum;
  tracksIncludeNewConstruction: boolean;
  initialIncludeNewConstruction: boolean;
  defaultIncludeNewConstruction: boolean;
};

export const analysisSettingsMachine = setup({
  types: {
    context: {} as AnalysisSettingsContext,
    events: {} as AnalysisSettingsEvent,
    input: {} as AnalysisSettingsMachineInput,
  },
  actors: {
    // Placeholders — replaced at runtime via machine.provide() in useAnalysisSettingsActor
    urlSync: urlSyncActorPlaceholder,
    localStorageSync: localStorageSyncActorPlaceholder,
  },
  guards: {
    isModified: ({ context }) => isModifiedContext(context),
  },
  actions: {
    /**
     * Single transition (not an event-handler array): XState + TS lose inference on
     * `LOCALSTORAGE_INITIALIZED: [ { guard, ... }, { ... } ]`, which cascades to broken
     * `matches()` and guard types. Behavior matches the old two-branch setup: apply merged
     * values, then write URL iff {@link shouldWriteUrlOnInitPredicate} (evaluated against
     * pre-assign context vs event).
     */
    'init-context': enqueueActions(({ context, event, enqueue }) => {
      if (event.type !== 'LOCALSTORAGE_INITIALIZED') return;
      const shouldWriteUrl = shouldWriteUrlOnInitPredicate(context, event);
      enqueue.assign({
        appliedDisplayBase: event.displayBase,
        pendingDisplayBase: event.displayBase,
        appliedReferenceArea: event.referenceArea,
        pendingReferenceArea: event.referenceArea,
        appliedEmissionFactorSource: event.emissionFactorSource,
        pendingEmissionFactorSource: event.emissionFactorSource,
        appliedIncludeNewConstruction: event.includeNewConstruction,
        pendingIncludeNewConstruction: event.includeNewConstruction,
      });
      if (!shouldWriteUrl) return;
      enqueue.sendTo('urlSync', ({ context: c }) => ({
        type: 'WRITE_URL' as const,
        ...(c.scope === 'statusQuo' && { displayBase: event.displayBase }),
        referenceArea: event.referenceArea,
        emissionFactorSource: event.emissionFactorSource,
        ...(c.tracksIncludeNewConstruction && {
          includeNewConstruction: event.includeNewConstruction,
        }),
      }));
    }),
    'change-pending-display-base': assign({
      pendingDisplayBase: ({ event }) => {
        if (event.type !== 'USER_CHANGE_PENDING_DISPLAY_BASE') return DEFAULT_DISPLAY_BASE;
        return event.displayBase;
      },
    }),
    'change-pending-reference-area': assign({
      pendingReferenceArea: ({ context, event }) => {
        if (event.type !== 'USER_CHANGE_PENDING_REFERENCE_AREA') return context.defaultReferenceArea;
        return event.referenceArea;
      },
    }),
    'change-pending-emission-factor-source': assign({
      pendingEmissionFactorSource: ({ event }) => {
        if (event.type !== 'USER_CHANGE_PENDING_EMISSION_FACTOR_SOURCE') return DEFAULT_EMISSION_FACTOR_SOURCE;
        return event.emissionFactorSource;
      },
    }),
    'change-pending-include-new-construction': assign({
      pendingIncludeNewConstruction: ({ event }) => {
        if (event.type !== 'USER_CHANGE_PENDING_INCLUDE_NEW_CONSTRUCTION') return true;
        return event.includeNewConstruction;
      },
    }),
    'sync-org-default-include-new-construction': assign({
      defaultIncludeNewConstruction: ({ context, event }) => {
        if (event.type !== 'ORG_DEFAULT_INCLUDE_PLANNED_UPDATED') return context.defaultIncludeNewConstruction;
        return event.defaultIncludeNewConstruction;
      },
    }),
    'commit-applied': assign({
      appliedDisplayBase: ({ context }) => context.pendingDisplayBase,
      appliedReferenceArea: ({ context }) => context.pendingReferenceArea,
      appliedEmissionFactorSource: ({ context }) => context.pendingEmissionFactorSource,
      appliedIncludeNewConstruction: ({ context }) => context.pendingIncludeNewConstruction,
    }),
    'reset-pending': assign({
      pendingDisplayBase: DEFAULT_DISPLAY_BASE,
      pendingReferenceArea: ({ context }) => context.defaultReferenceArea,
      pendingEmissionFactorSource: DEFAULT_EMISSION_FACTOR_SOURCE,
      pendingIncludeNewConstruction: ({ context }) =>
        context.tracksIncludeNewConstruction
          ? context.defaultIncludeNewConstruction
          : context.pendingIncludeNewConstruction,
    }),
    'cancel-pending': assign({
      pendingDisplayBase: ({ context }) => context.appliedDisplayBase,
      pendingReferenceArea: ({ context }) => context.appliedReferenceArea,
      pendingEmissionFactorSource: ({ context }) => context.appliedEmissionFactorSource,
      pendingIncludeNewConstruction: ({ context }) =>
        context.tracksIncludeNewConstruction
          ? context.appliedIncludeNewConstruction
          : context.pendingIncludeNewConstruction,
    }),
    'close-popover-from-event': ({ event }) => {
      if (event.type === 'USER_APPLY' || event.type === 'USER_CLOSE') {
        event.close();
      }
    },
    /** No-op default; override with `machine.provide()` to run analytics when `USER_APPLY` passes `isModified`. */
    'track-display-base-changed': () => {},
  },
}).createMachine({
  context: ({ input }) => ({
    scope: input.scope,
    tracksIncludeNewConstruction: input.tracksIncludeNewConstruction,
    appliedDisplayBase: input.initialDisplayBase,
    pendingDisplayBase: input.initialDisplayBase,
    appliedReferenceArea: input.initialReferenceArea,
    pendingReferenceArea: input.initialReferenceArea,
    defaultReferenceArea: input.defaultReferenceArea,
    appliedEmissionFactorSource: input.initialEmissionFactorSource,
    pendingEmissionFactorSource: input.initialEmissionFactorSource,
    appliedIncludeNewConstruction: input.initialIncludeNewConstruction,
    pendingIncludeNewConstruction: input.initialIncludeNewConstruction,
    defaultIncludeNewConstruction: input.defaultIncludeNewConstruction,
  }),
  id: 'analysisSettingsMachine',
  initial: 'initializing',
  invoke: [
    {
      id: 'urlSync',
      src: 'urlSync',
      input: ({ context }) => ({
        initialDisplayBase: context.appliedDisplayBase,
        initialReferenceArea: context.appliedReferenceArea,
        initialEmissionFactorSource: context.appliedEmissionFactorSource,
      }),
    },
    {
      id: 'localStorageSync',
      src: 'localStorageSync',
      input: () => ({}) as Record<string, never>,
    },
  ],
  states: {
    /**
     * Entry state. Waits for LOCALSTORAGE_INITIALIZED, which is the authoritative
     * initial value — localStorageSyncActor merges URL + stored preferences with
     *  the correct priority (explicit URL > localStorage > tab default).
     *
     * User interaction events are silently ignored while initializing.
     *
     * On LOCALSTORAGE_INITIALIZED:
     * - statusQuo: always writes URL so referenceAreaType is present for queries
     *   that skip when the param is absent (no withDefault in nuqs schema).
     * - actionPlanning: writes URL only when the stored value differs from the
     *   URL-sourced initial value, to avoid overwriting scenario URL params on
     *   every mount.
     * - When tracking new construction, URL write also when merged include flag differs
     *   from the URL-derived initial (see `shouldWriteUrlOnInitPredicate` in `init-context`).
     */
    initializing: {
      on: {
        ORG_DEFAULT_INCLUDE_PLANNED_UPDATED: {
          actions: 'sync-org-default-include-new-construction',
        },
        LOCALSTORAGE_INITIALIZED: {
          actions: 'init-context',
          target: 'applied',
        },
      },
    },

    /**
     * Settled state — no uncommitted changes.
     */
    applied: {
      on: {
        ORG_DEFAULT_INCLUDE_PLANNED_UPDATED: {
          actions: 'sync-org-default-include-new-construction',
        },
        USER_CHANGE_PENDING_DISPLAY_BASE: {
          actions: 'change-pending-display-base',
          target: 'pending',
        },
        USER_CHANGE_PENDING_REFERENCE_AREA: {
          actions: 'change-pending-reference-area',
          target: 'pending',
        },
        USER_CHANGE_PENDING_EMISSION_FACTOR_SOURCE: {
          actions: 'change-pending-emission-factor-source',
          target: 'pending',
        },
        USER_CHANGE_PENDING_INCLUDE_NEW_CONSTRUCTION: {
          guard: ({ context }) => context.tracksIncludeNewConstruction,
          actions: 'change-pending-include-new-construction',
          target: 'pending',
        },
        USER_RESET: {
          actions: 'reset-pending',
          target: 'pending',
        },
        USER_CLOSE: {
          actions: ['cancel-pending', 'close-popover-from-event'],
        },
      },
    },
    /**
     * The popover is open with uncommitted changes.
     */
    pending: {
      on: {
        ORG_DEFAULT_INCLUDE_PLANNED_UPDATED: {
          actions: 'sync-org-default-include-new-construction',
        },
        USER_CHANGE_PENDING_DISPLAY_BASE: {
          actions: 'change-pending-display-base',
        },
        USER_CHANGE_PENDING_REFERENCE_AREA: {
          actions: 'change-pending-reference-area',
        },
        USER_CHANGE_PENDING_EMISSION_FACTOR_SOURCE: {
          actions: 'change-pending-emission-factor-source',
        },
        USER_CHANGE_PENDING_INCLUDE_NEW_CONSTRUCTION: {
          guard: ({ context }) => context.tracksIncludeNewConstruction,
          actions: 'change-pending-include-new-construction',
        },
        USER_APPLY: {
          guard: 'isModified',
          actions: [
            'track-display-base-changed',
            sendTo('urlSync', ({ context }) => ({
              type: 'WRITE_URL' as const,
              ...(context.scope === 'statusQuo' && { displayBase: context.pendingDisplayBase }),
              referenceArea: context.pendingReferenceArea,
              emissionFactorSource: context.pendingEmissionFactorSource,
              ...(context.tracksIncludeNewConstruction && {
                includeNewConstruction: context.pendingIncludeNewConstruction,
              }),
            })),
            sendTo('localStorageSync', ({ context }) => ({
              type: 'WRITE_LOCALSTORAGE' as const,
              displayBase: context.pendingDisplayBase,
              referenceArea: context.pendingReferenceArea,
              emissionFactorSource: context.pendingEmissionFactorSource,
              includeNewConstruction: context.pendingIncludeNewConstruction,
            })),
            'commit-applied',
            'close-popover-from-event',
          ],
          target: 'applied',
        },
        USER_RESET: {
          actions: 'reset-pending',
        },
        USER_CLOSE: {
          actions: ['cancel-pending', 'close-popover-from-event'],
          target: 'applied',
        },
      },
    },
  },
});
